import React, { useEffect, useRef, useState } from "react";
import SlideOver from "./SlideOver";
import { Box, Stack, Typography } from "@mui/material";
import UserMessage from "./UserMessage";
import AIMessage from "./AIMessage";
import ChatBox from "./ChatBox";
import NewChatScreen from "./NewChatScreen";
import ChatSearchHeader from "./ChatSearchHeader";
import { parse, isToday, isYesterday, format } from "date-fns";
import axios from "axios";
import ConversationList from "./ConversationList";
import Disclaimer from "./Disclaimer";
import ChatTopBar from "./components/ChatTopBar";
import { DJANGO_AI_URL } from "../../api/config";
import BlinkingDot from "./BlinkingDot";
import AILoader from "./AILoader";
import ConversationListLoader from "./ConversationListLoader";
import ConversationLoadingPlaceholder from "./ConversationLoadingPlaceholder";
import ConversationTypingLoader from "./ConversationTypingLoader";

const FlexiAI = ({ open, onClose }) => {
  const queryParams = new URLSearchParams(window.location.search);
  const hotelId = queryParams.get("hotelId");
  const hotelEmail = localStorage.getItem("HotelEmail");
  const email = localStorage.getItem("email");
  const token = localStorage.getItem("token");

  console.log("hotel email: ", hotelEmail, token, DJANGO_AI_URL);
  const [userPrompt, setUserPrompt] = useState("");
  const [conversationList, setConversationList] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [activeConversationHistory, setActiveConversationHistory] = useState(
    []
  );
  const [openConversationList, setOpenConversationList] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [loadingFlexiAiResponse, setLoadingFlexiAiResponse] = useState(false);
  const [loadingConversationList, setLoadingConversationList] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const messagesEndRef = useRef(null);
  useEffect(() => {
    // Delay to ensure DOM has rendered
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 0);
  }, [activeConversationHistory]);

  // get array of todays, yesterdays and last 7 days conversations
  const calculateConversationList = (conversationList) => {
    const todaysConversations = [];
    const yesterdaysConversations = [];
    const pastConversations = [];
    for (const conversation of conversationList) {
      // Parse string "2025-04-15 09:10:00" into a Date object
      const createdAt = parse(
        conversation.created_at,
        "yyyy-MM-dd HH:mm:ss",
        new Date()
      );

      if (isToday(createdAt)) {
        todaysConversations.push(conversation);
      } else if (isYesterday(createdAt)) {
        yesterdaysConversations.push(conversation);
      } else {
        pastConversations.push(conversation);
      }
    }
    return {
      todaysConversations,
      yesterdaysConversations,
      pastConversations,
    };
  };
  const { todaysConversations, yesterdaysConversations, pastConversations } =
    calculateConversationList(conversationList);

  // api: create conversation
  const createConversation = async (userPrompt) => {
    const response = await axios.post(
      DJANGO_AI_URL +
        `/ai-assistant/createConversation?hotel_id=${hotelId}&user_email=${hotelEmail}`,
      {
        conversation_name: userPrompt.slice(0, 20),
      }
    );
    return response;
  };

  // api: get conversaitons
  const getConversations = async () => {
    const response = await axios.get(
      DJANGO_AI_URL +
        `/ai-assistant/getConversations?hotel_id=${hotelId}&user_email=${hotelEmail}`,
      {
        headers: { Authorization: token },
      }
    );
    return response;
  };

  // api: send user prompt
  const sendUserPrompt = async (userPrompt) => {
    const response = await axios.post(
      DJANGO_AI_URL +
        `/ai-assistant/user-query?hotel_id=${hotelId}&conversation_id=${activeConversation.conversation_id}&user_email=${hotelEmail}`,
      {
        query: userPrompt,
      }
    );
    return response;
  };

  // api: fetch messages
  const fetchMessages = async () => {
    const response = await axios.get(
      DJANGO_AI_URL +
        `/ai-assistant/getMessages?hotel_id=${hotelId}&user_email=${hotelEmail}&conversation_id=${activeConversation.conversation_id}`
    );
    return response;
  };

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoadingConversationList(true);
        const response = await getConversations();
        const sortedConversationList = [
          ...response.data.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          ),
        ];
        if (sortedConversationList.length === 0) {
          const newConversation = {
            conversation_id: 1,
            conversation_name: "Untitled",
            created_at: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
          };
          setConversationList([newConversation]);
          setActiveConversation(newConversation);
        } else {
          setConversationList(sortedConversationList);
          setActiveConversation(sortedConversationList[0]);
        }
      } catch (e) {
        console.log("abhishek ", e);
      } finally {
        setLoadingConversationList(false);
      }
    };
    fetchConversations();
  }, []);
  useEffect(() => {
    console.log("changed active conversation");
    if (
      activeConversation == null ||
      activeConversation.conversation_name == "Untitled"
    ) {
      setActiveConversationHistory([]);
      return null;
    }
    const fetchActiveConversationHistory = async () => {
      try {
        setLoadingMessages(true);
        const response = await fetchMessages();
        setActiveConversationHistory(response.data);
      } catch (e) {
        console.log("error fetching conversation history ", e);
      } finally {
        setLoadingMessages(false);
      }
    };
    fetchActiveConversationHistory();
  }, [activeConversation]);

  // clear user input
  // add user prompt to conversation history
  // if the conversation name is Untitled then first create conversation
  const sendUserQuery = async (userPrompt) => {
    setUserPrompt("");
    setActiveConversationHistory([
      ...activeConversationHistory,
      {
        user_type: "user",
        message: userPrompt,
      },
    ]);
    try {
      setLoadingFlexiAiResponse(true);
      if (
        !activeConversation ||
        activeConversation.conversation_name == "Untitled"
      ) {
        const response = await createConversation(userPrompt);
        const newConversation = {
          conversation_id: response.data.id,
          conversation_name: response.data.conversation_name,
          created_at: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
        };
        const newConversationList = [...conversationList];
        newConversationList[0].conversation_id =
          newConversation.conversation_id;
        newConversationList[0].conversation_name =
          newConversation.conversation_name;
        setConversationList(newConversationList);
      }
      const response = await sendUserPrompt(userPrompt);
      setActiveConversationHistory((ach) => [
        ...ach,
        {
          user_type: "assistant",
          message: response.data.response,
        },
      ]);
    } catch (e) {
      console.log("error getting response: ", e);
    } finally {
      setLoadingFlexiAiResponse(false);
    }
  };

  // if first chat item is untitled then dont create a new chat
  // else create a new chat and store in front of all chats
  // set active chat to this
  // and clear all messages
  const createChatItem = async () => {
    if (conversationList.length == 0) {
      const newConversation = {
        conversation_id: 1,
        conversation_name: "Untitled",
        created_at: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
      };
      setConversationList([newConversation]);
      setActiveConversation(newConversation);
      setActiveConversationHistory([]);
      return;
    }
    if (conversationList[0].conversation_name == "Untitled") return;
    const newConversation = {
      conversation_id: conversationList[0].conversation_id + 1,
      conversation_name: "Untitled",
      created_at: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
    };
    setConversationList((cl) => [newConversation, ...cl]);
    setActiveConversation(newConversation);
    setActiveConversationHistory([]);
  };

  const groupedConversations = [
    { label: "Today", data: todaysConversations },
    { label: "Yesterday", data: yesterdaysConversations },
    { label: "Last 7 days", data: pastConversations },
  ];
  console.log("conversation list: ", conversationList);
  console.log("activeConversationHistory: ", activeConversationHistory);

  const onSelectConversation = (conversation) => {
    setActiveConversation(conversation);
    setOpenConversationList(false);
  };

  const onSuggestionSelect = (text) => {
    sendUserQuery(text);
  };

  // api: delete conversation
  const deleteConversation = async (conversation_id) => {
    const response = await axios.post(
      DJANGO_AI_URL +
        `/ai-assistant/deleteConversation?hotel_id=${hotelId}&user_email=${hotelEmail}`,
      {
        conversation_id: conversation_id,
      }
    );
    return response;
  };
  const handleDeleteConversation = async (conversation) => {
    if (
      conversationList.length === 1 &&
      conversation.conversation_name === "Untitled"
    ) {
      return;
    }

    const deletedConversation = conversation;

    const updatedList = conversationList.filter(
      (convo) => convo.conversation_id !== conversation.conversation_id
    );

    // Set new active conversation if the one deleted was active
    if (activeConversation?.conversation_id === conversation.conversation_id) {
      console.log("conversation list: ", updatedList);
      setActiveConversation(updatedList[0]);
    }

    setConversationList(updatedList);

    // Don't make API call for default "Untitled" conversation
    if (conversation.conversation_name === "Untitled") {
      return;
    }

    try {
      await deleteConversation(conversation.conversation_id);
    } catch (e) {
      console.log("Error deleting conversation", e);

      // Rollback if API deletion fails
      setConversationList((currentList) => {
        const rolledBackList = [...currentList, deletedConversation].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        // If the deleted conversation was active, restore it
        if (
          activeConversation?.conversation_id ===
          deletedConversation.conversation_id
        ) {
          setActiveConversation(deletedConversation);
        }
        return rolledBackList;
      });
    }
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      width={isFullScreen ? "95vw" : "30vw"}
    >
      <Box
        display="flex"
        flexDirection="column"
        height="100vh" // Set full viewport height
        position="relative"
      >
        {/* Header - Fixed at top */}
        <ChatTopBar
          toggleFullScreen={() => setIsFullScreen((ifs) => !ifs)}
          onClose={onClose}
          isFullScreen={isFullScreen}
          onAddChat={createChatItem}
          showAddChat={!openConversationList}
          toggleChatsListPanel={() => setOpenConversationList((ocl) => !ocl)}
        />

        {/* Body - Takes remaining space */}
        <Box display="flex" flex="1" position="relative" minHeight={0}>
          {/* Left Panel */}
          {isFullScreen ? (
            <Stack
              width="20%"
              borderRight="1px solid #eee"
              direction="column"
              spacing={3}
              height="100%" // Full height of the body section
            >
              <ChatSearchHeader onAddChat={createChatItem} />

              {/* Chat list */}
              <Box flex="1" overflow="auto">
                <ConversationList
                  groupedConversations={groupedConversations}
                  activeConversation={activeConversation}
                  onSelectConversation={onSelectConversation}
                  onDeleteConversation={handleDeleteConversation}
                />
              </Box>
            </Stack>
          ) : openConversationList ? (
            <Stack
              width="100%"
              borderRight="1px solid #eee"
              direction="column"
              spacing={3}
              zIndex={99}
              position="absolute"
              top={0}
              left={0}
              height="100%"
              backgroundColor="white"
            >
              <ChatSearchHeader onAddChat={createChatItem} />

              {/* Chat list */}
              <Box flex="1" overflow="auto">
                <ConversationList
                  groupedConversations={groupedConversations}
                  activeConversation={activeConversation}
                  onSelectConversation={onSelectConversation}
                />
              </Box>
            </Stack>
          ) : null}

          {/* Right Panel */}
          <Box
            width={isFullScreen ? "80%" : "100%"}
            display="flex"
            flexDirection="column"
            flex={1}
            px={isFullScreen ? 16 : 1}
            minHeight={0} // Important for flex children with overflow
          >
            {/* Chat history - Scrollable middle section */}
            <Box
              flex="1"
              overflow="auto"
              py={2}
              pb={8}
              minHeight={0} // Important for nested flex layouts
            >
              {loadingMessages || loadingConversationList ? (
                // Show aesthetic loader while conversation is loading
                // <ConversationLoadingPlaceholder />
                <ConversationTypingLoader />
              ) : activeConversationHistory &&
                activeConversationHistory.length === 0 ? (
                <NewChatScreen onSuggestionSelect={onSuggestionSelect} />
              ) : (
                <>
                  {activeConversationHistory?.map((message, index) => {
                    if (message.user_type === "user") {
                      return (
                        <UserMessage key={index} content={message.message} />
                      );
                    } else if (message.user_type === "assistant") {
                      return (
                        <AIMessage key={index} content={message.message} />
                      );
                    }
                    return null;
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}

              {/* Flexi AI is typing... */}
              {loadingFlexiAiResponse && <AILoader />}
            </Box>

            {/* Chat input and disclaimer - Fixed at bottom */}
            <Box py={1}>
              <ChatBox
                prompt={userPrompt}
                updatePrompt={(value) => setUserPrompt(value)}
                onSend={() => sendUserQuery(userPrompt)}
              />
              <Disclaimer />
            </Box>
          </Box>
        </Box>
      </Box>
    </SlideOver>
  );
};

export default FlexiAI;
